// Import shared enums from other features
import { DifficultyLevel, getDifficultyLevelName } from '../platformExercises/types';

// Template Scope enum matching backend
export enum TemplateScope {
  Instructor = 1,  // Only visible to creator
  Studio = 2,      // Visible to all instructors in studio
  Global = 3       // Platform-wide visibility
}

// Template Structure enum matching backend
export enum TemplateStructure {
  FlowSequence = 1,      // Continuous flow from start to finish
  CircuitRotation = 2,   // Stations/circuits with rotations
  BlockBased = 3,        // Structured blocks with specific purposes
  FreeForm = 4           // Flexible structure, instructor-led
}

// Helper functions for display names
export const getTemplateScopeName = (scope: TemplateScope): string => {
  switch (scope) {
    case TemplateScope.Instructor:
      return 'Instructor';
    case TemplateScope.Studio:
      return 'Studio';
    case TemplateScope.Global:
      return 'Global';
    default:
      return 'Unknown';
  }
};

export const getTemplateStructureName = (structure: TemplateStructure): string => {
  switch (structure) {
    case TemplateStructure.FlowSequence:
      return 'Flow Sequence';
    case TemplateStructure.CircuitRotation:
      return 'Circuit Rotation';
    case TemplateStructure.BlockBased:
      return 'Block Based';
    case TemplateStructure.FreeForm:
      return 'Free Form';
    default:
      return 'Unknown';
  }
};

// Re-export DifficultyLevel and helper for consistency
export { DifficultyLevel, getDifficultyLevelName };

// Template Block definition
export interface CreateTemplateBlockRequest {
  blockId: number;
  orderIndex: number;
  isOptional: boolean;
  overrideDurationMinutes?: number;
  notes?: string;
  transitionSeconds: number;
}

export interface TemplateBlockDetailResponse {
  id: number;
  blockId: number;
  blockName: string;
  orderIndex: number;
  isOptional: boolean;
  overrideDurationMinutes?: number;
  notes?: string;
  transitionSeconds: number;
}

// Template Exercise definition
export interface CreateTemplateExerciseRequest {
  exerciseId: number;
  orderIndex: number;
  durationSeconds?: number;
  repetitions?: number;
  springSettings?: string;
  equipmentSetupId?: number;
  notes?: string;
  transitionSeconds: number;
  isOptional: boolean;
}

export interface TemplateExerciseDetailResponse {
  id: number;
  exerciseId: number;
  exerciseName: string;
  orderIndex: number;
  durationSeconds?: number;
  repetitions?: number;
  springSettings?: string;
  equipmentSetupId?: number;
  notes?: string;
  transitionSeconds: number;
  isOptional: boolean;
}

// API Request/Response Types
export interface CreatePlatformTemplateRequest {
  name: string;
  description?: string;
  durationMinutes: number;
  primaryEquipmentId?: number;
  difficulty: DifficultyLevel;
  scope: TemplateScope;
  structure: TemplateStructure;
  maxParticipants?: number;
  tags?: string;
  originalAuthor?: string;
  parentTemplateId?: number;
  blocks?: CreateTemplateBlockRequest[];
  exercises?: CreateTemplateExerciseRequest[];
}

export interface UpdatePlatformTemplateRequest {
  name?: string;
  description?: string;
  durationMinutes: number;
  primaryEquipmentId?: number;
  difficulty: DifficultyLevel;
  scope: TemplateScope;
  structure: TemplateStructure;
  maxParticipants?: number;
  tags?: string;
  originalAuthor?: string;
  isActive: boolean;
  blocks?: CreateTemplateBlockRequest[];
  exercises?: CreateTemplateExerciseRequest[];
}

export interface PlatformTemplateSummaryResponse {
  id: number;
  name: string;
  description?: string;
  durationMinutes: number;
  difficulty: DifficultyLevel;
  scope: TemplateScope;
  structure: TemplateStructure;
  primaryEquipmentName?: string;
  blockCount: number;
  exerciseCount: number;
  maxParticipants?: number;
  isActive: boolean;
  tags?: string;
  createdBy?: string;
  originalAuthor?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface PlatformTemplateDetailResponse {
  id: number;
  name: string;
  description?: string;
  durationMinutes: number;
  primaryEquipmentId?: number;
  primaryEquipmentName?: string;
  difficulty: DifficultyLevel;
  scope: TemplateScope;
  structure: TemplateStructure;
  maxParticipants?: number;
  isActive: boolean;
  tags?: string;
  createdBy?: string;
  originalAuthor?: string;
  isPlatformTemplate: boolean;
  parentTemplateId?: number;
  createdAt: string;
  updatedAt?: string;
  blocks: TemplateBlockDetailResponse[];
  exercises: TemplateExerciseDetailResponse[];
}

// Paginated API response
export interface PlatformTemplateListResponse {
  templates: PlatformTemplateSummaryResponse[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

// Form data type for the template form
export interface TemplateFormData {
  name: string;
  description: string;
  durationMinutes: number;
  primaryEquipmentId?: number;
  difficulty: DifficultyLevel;
  scope: TemplateScope;
  structure: TemplateStructure;
  maxParticipants?: number;
  tags: string;
  originalAuthor: string;
  isActive?: boolean;
  blocks: TemplateBlockFormData[];
  exercises: TemplateExerciseFormData[];
}

// Block form data within template
export interface TemplateBlockFormData {
  blockId: number;
  blockName?: string; // For display purposes
  orderIndex: number;
  isOptional: boolean;
  overrideDurationMinutes?: number;
  notes: string;
  transitionSeconds: number;
}

// Exercise form data within template
export interface TemplateExerciseFormData {
  exerciseId: number;
  exerciseName?: string; // For display purposes
  orderIndex: number;
  durationSeconds?: number;
  repetitions?: number;
  springSettings: string;
  equipmentSetupId?: number;
  notes: string;
  transitionSeconds: number;
  isOptional: boolean;
}

// Block option for dropdowns (reuse from platformBlocks)
export interface BlockOption {
  id: number;
  name: string;
  category: string;
  difficulty: string;
  durationMinutes: number;
  exerciseCount: number;
}

// Exercise option for dropdowns (reuse from platformExercises)
export interface ExerciseOption {
  id: number;
  name: string;
  category: string;
  difficulty: string;
  primaryEquipmentName: string;
  typicalDurationSeconds?: number;
  defaultReps?: number;
}

// Equipment option for dropdowns (reuse from platformEquipment)
export interface EquipmentOption {
  id: number;
  name: string;
  category: string;
  requiresSetup: boolean;
}